import { Controller } from '@hotwired/stimulus'
import { controller } from '../../lib/dom'

export default class extends Controller {
  static targets = ['track', 'slide', 'counter', 'dot', 'prev', 'next']
  static values = { startIndex: Number }

  connect() {
    if (!this.hasTrackTarget) return

    this.boundOnScroll = this.onScroll.bind(this)
    this.trackTarget.addEventListener('scroll', this.boundOnScroll, {
      passive: true,
    })

    this.restorePosition()
  }

  disconnect() {
    if (this.hasTrackTarget && this.boundOnScroll) {
      this.trackTarget.removeEventListener('scroll', this.boundOnScroll)
    }
    if (this.initRaf) cancelAnimationFrame(this.initRaf)
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf)
  }

  // Jump straight to the tapped photo once the track has its layout width
  restorePosition() {
    if (this.trackTarget.clientWidth === 0) {
      this.initRaf = requestAnimationFrame(() => this.restorePosition())
      return
    }

    this.scrollToIndex(this.startIndexValue)
    this.update()
  }

  previous() {
    this.scrollToIndex(this.activeIndex() - 1, { smooth: true })
  }

  next() {
    this.scrollToIndex(this.activeIndex() + 1, { smooth: true })
  }

  goTo(event) {
    this.scrollToIndex(Number(event.currentTarget.dataset.index), {
      smooth: true,
    })
  }

  onScroll() {
    if (this.scrollRaf) return

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null
      this.update()
    })
  }

  scrollToIndex(index, { smooth = false } = {}) {
    const slide = this.slideTargets[index]
    if (!slide) return

    const left =
      slide.offsetLeft - (this.trackTarget.clientWidth - slide.clientWidth) / 2

    if (smooth) {
      this.trackTarget.scrollTo({ left, behavior: 'smooth' })
    } else {
      this.trackTarget.scrollLeft = left
    }
  }

  zoomChanged(event) {
    this.element.classList.toggle(
      'ma-photo-viewer--zoomed',
      event.detail.zoomed
    )
  }

  update() {
    const index = this.activeIndex()
    const last = this.slideTargets.length - 1

    if (index !== this.lastIndex) {
      this.lastIndex = index
      this.resetZoomExcept(index)
    }

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1}/${this.slideTargets.length}`
    }

    this.dotTargets.forEach((dot, dotIndex) => {
      dot.classList.toggle('ma-photo-viewer__dot--active', dotIndex === index)
    })

    // hidden rather than dead at the ends: an arrow that does nothing reads as broken
    if (this.hasPrevTarget) this.prevTarget.disabled = index === 0
    if (this.hasNextTarget) this.nextTarget.disabled = index === last
  }

  // A photo keeps its zoom on screen, and starts fresh once left behind
  resetZoomExcept(index) {
    this.slideTargets.forEach((slide, slideIndex) => {
      if (slideIndex !== index) controller(slide, 'photo-zoom')?.reset()
    })
  }

  // The slide whose centre is closest to the viewport centre
  activeIndex() {
    const center =
      this.trackTarget.scrollLeft + this.trackTarget.clientWidth / 2
    let activeIndex = 0
    let minDistance = Infinity

    this.slideTargets.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2
      const distance = Math.abs(slideCenter - center)

      if (distance < minDistance) {
        minDistance = distance
        activeIndex = index
      }
    })

    return activeIndex
  }
}
